<template>
	<section>
		<!-- <h2 class="onboarding-title">Welcome to GrubVision</h2> -->
		<p class="onboarding-text lead">
			Thanks for joining us! We're here to help you make confident choices and
			stay energized on your nutritional journey.
		</p>
		<p class="onboarding-text">
			We'll walk through a few quick questions so we can map the experience to
			what matters most to you.
		</p>
		<div class="illustration food-bowl">
			<div class="plate-shadow"></div>
			<div class="plate-rim"></div>
			<div class="bowl">
				<div class="broth"></div>
				<div class="broth-shine"></div>
			</div>
			<div class="leaf leaf-left"></div>
			<div class="leaf leaf-right"></div>
			<div class="ingredient ingredient-egg"></div>
			<div class="ingredient ingredient-grain"></div>
			<div class="ingredient ingredient-tomato"></div>
			<div class="sprinkle sprinkle-1"></div>
			<div class="sprinkle sprinkle-2"></div>
			<div class="sprinkle sprinkle-3"></div>
			<div class="steam steam-1"></div>
			<div class="steam steam-2"></div>
		</div>
	</section>
</template>

<script setup></script>

<style scoped>
	.illustration {
		position: relative;
		width: 140px;
		height: 140px;
		margin: 1rem auto 0;
	}

	.food-bowl .plate-shadow {
		position: absolute;
		bottom: 4px;
		left: 50%;
		width: 140px;
		height: 40px;
		border-radius: 50%;
		background: radial-gradient(
			circle,
			rgba(15, 23, 42, 0.18),
			rgba(15, 23, 42, 0)
		);
		transform: translateX(-50%);
		filter: blur(2px);
		animation: shadowPulse 6s ease-in-out infinite;
	}

	.food-bowl .plate-rim {
		position: absolute;
		bottom: 14px;
		left: 50%;
		width: 128px;
		height: 128px;
		border-radius: 50%;
		background: linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.8),
			rgba(226, 232, 240, 0.9)
		);
		box-shadow: inset 0 10px 18px rgba(203, 213, 225, 0.5),
			0 15px 35px rgba(15, 23, 42, 0.2);
		transform: translateX(-50%);
		z-index: 1;
	}

	.food-bowl .plate-rim::after {
		content: "";
		position: absolute;
		inset: 16px;
		border-radius: 50%;
		background: linear-gradient(
			135deg,
			rgba(241, 245, 249, 0.75),
			rgba(255, 255, 255, 0.95)
		);
		box-shadow: inset 0 4px 8px rgba(255, 255, 255, 0.6);
	}

	.food-bowl .bowl {
		position: absolute;
		bottom: 36px;
		left: 50%;
		width: 118px;
		height: 92px;
		border-radius: 55% 55% 45% 45%;
		background: linear-gradient(135deg, #f0f9ff, #c7d2fe);
		transform: translateX(-50%);
		box-shadow: inset 0 -12px 18px rgba(30, 64, 175, 0.18),
			0 8px 20px rgba(79, 70, 229, 0.18);
		z-index: 2;
		overflow: hidden;
	}

	.food-bowl .bowl::before {
		content: "";
		position: absolute;
		top: -10px;
		left: 50%;
		width: 120%;
		height: 50%;
		transform: translateX(-50%);
		border-radius: 50%;
		background: linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.85),
			rgba(226, 232, 240, 0)
		);
	}

	.food-bowl .broth {
		position: absolute;
		inset: 16px 14px 28px;
		border-radius: 45% 55% 50% 50%;
		background: radial-gradient(
			circle at 30% 30%,
			#fde68a,
			#f97316 65%,
			#ea580c 100%
		);
		box-shadow: inset 0 6px 12px rgba(249, 115, 22, 0.35);
		animation: brothGlow 6s ease-in-out infinite;
	}

	.food-bowl .broth-shine {
		position: absolute;
		top: 18px;
		right: 20px;
		width: 38px;
		height: 22px;
		border-radius: 50%;
		background: radial-gradient(
			circle,
			rgba(255, 255, 255, 0.75),
			rgba(255, 255, 255, 0)
		);
		opacity: 0.7;
	}

	.leaf {
		position: absolute;
		width: 48px;
		height: 24px;
		border-radius: 50% 50% 45% 45%;
		background: linear-gradient(135deg, #22c55e, #16a34a);
		box-shadow: inset 0 6px 10px rgba(21, 128, 61, 0.35);
		z-index: 4;
		--base-angle: 0deg;
		animation: leafSway 5s ease-in-out infinite;
		transform: rotate(var(--base-angle));
	}

	.leaf::after {
		content: "";
		position: absolute;
		inset: 6px 18px;
		border-radius: 50%;
		background: rgba(34, 197, 94, 0.55);
	}

	.leaf-left {
		top: 14px;
		left: 10px;
		transform-origin: right center;
		--base-angle: -8deg;
		animation-delay: 0.2s;
	}

	.leaf-right {
		top: 20px;
		right: 6px;
		transform-origin: left center;
		--base-angle: 10deg;
		animation-delay: 1s;
	}

	.ingredient {
		position: absolute;
		border-radius: 50%;
		box-shadow: 0 8px 16px rgba(15, 23, 42, 0.18);
		z-index: 5;
		animation: ingredientBob 5.5s ease-in-out infinite;
	}

	.ingredient-egg {
		width: 42px;
		height: 42px;
		background: radial-gradient(
			circle at 40% 35%,
			#fef3c7 25%,
			#facc15 40%,
			#f59e0b 70%,
			#b45309 100%
		);
		top: 38px;
		left: 26px;
		animation-delay: 0.4s;
	}

	.ingredient-grain {
		width: 36px;
		height: 28px;
		border-radius: 50% 55% 45% 55%;
		background: linear-gradient(135deg, #fbbf24, #d97706);
		top: 58px;
		right: 30px;
		animation-delay: 1.2s;
	}

	.ingredient-tomato {
		width: 24px;
		height: 24px;
		background: radial-gradient(
			circle at 30% 30%,
			#fda4af,
			#f43f5e 65%,
			#be123c 100%
		);
		top: 20px;
		right: 46px;
		animation-delay: 2s;
	}

	.sprinkle {
		position: absolute;
		width: 10px;
		height: 10px;
		border-radius: 50%;
		background: radial-gradient(
			circle,
			rgba(255, 255, 255, 0.9),
			rgba(148, 163, 184, 0)
		);
		box-shadow: 0 0 10px rgba(125, 211, 252, 0.6);
		z-index: 6;
		animation: sprinkleDrift 4s ease-in-out infinite;
	}

	.sprinkle-1 {
		top: 12px;
		left: 48%;
	}

	.sprinkle-2 {
		top: 44px;
		left: 12%;
		animation-delay: 1.2s;
	}

	.sprinkle-3 {
		top: 68px;
		right: 18%;
		animation-delay: 2.2s;
	}

	.steam {
		position: absolute;
		bottom: 72px;
		left: 50%;
		width: 8px;
		height: 42px;
		border-radius: 999px;
		background: linear-gradient(
			180deg,
			rgba(255, 255, 255, 0.65),
			rgba(148, 163, 184, 0)
		);
		transform: translate(-50%, 16px);
		animation: steamRise 4.5s ease-in-out infinite;
		z-index: 4;
	}

	.steam-1 {
		margin-left: -18px;
		animation-delay: 0.4s;
	}

	.steam-2 {
		margin-left: 12px;
		height: 28px;
		animation-delay: 1.2s;
	}

	@media (max-width: 768px) {
		.illustration {
			width: 120px;
			height: 120px;
			margin-top: 0.75rem;
		}
	}

	@keyframes shadowPulse {
		0%,
		100% {
			transform: translateX(-50%) scaleX(1);
			opacity: 0.85;
		}
		50% {
			transform: translateX(-50%) scaleX(1.08);
			opacity: 1;
		}
	}

	@keyframes brothGlow {
		0%,
		100% {
			filter: saturate(1);
		}
		50% {
			filter: saturate(1.15);
		}
	}

	@keyframes leafSway {
		0%,
		100% {
			transform: rotate(var(--base-angle));
		}
		50% {
			transform: rotate(calc(var(--base-angle) + 6deg));
		}
	}

	@keyframes ingredientBob {
		0%,
		100% {
			transform: translate3d(0, 0, 0) scale(1);
		}
		50% {
			transform: translate3d(8px, -12px, 0) scale(1.08);
		}
	}

	@keyframes sprinkleDrift {
		0%,
		100% {
			transform: translate3d(0, 0, 0) scale(0.9);
			opacity: 0.6;
		}
		50% {
			transform: translate3d(-4px, -8px, 0) scale(1.1);
			opacity: 1;
		}
	}

	@keyframes steamRise {
		0% {
			transform: translate(-50%, 18px) scaleY(0.85);
			opacity: 0;
		}
		30% {
			opacity: 0.4;
		}
		60% {
			opacity: 0.65;
		}
		100% {
			transform: translate(-50%, -26px) scaleY(1.05);
			opacity: 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.food-bowl .plate-shadow,
		.food-bowl .broth,
		.leaf,
		.ingredient,
		.sprinkle,
		.steam {
			animation: none !important;
		}
	}
</style>
